/**
 * @file routes/_app/audit-tasks/index.tsx
 * @description 稽核任務列表頁。
 *
 * 功能：
 *  - Segmented 過濾：我的 / 全部（admin/manager/decider 可切；auditor 鎖定「我的」）
 *  - 任務狀態下拉過濾（待派案 / 已派案 / 已確認 / 計畫中 / 現場中 / 報告中 / 完成 / 退回改派）
 *  - 序48：「狀態」欄改為「接收狀態」（依派案回覆推導，見 task-list-helpers）；新增
 *    「驗證類型」「稽核完成日」（稽核任務書完成時間，距完成日 7 天內紅色提示）
 *  - 伺服器端分頁（page / pageSize 進 queryKey，Table 受控分頁）
 *  - 點列進入任務詳情頁 `/audit-tasks/$taskId`
 *  - 「補建任務」按鈕（admin/manager 可見，異常補救用）：任務正常由派案「確認派遣」後
 *    自動流轉建立；僅當既有已派案案件因故未自動建立時，才在此手動補建
 *
 * 數據來源：GET /api/audit-tasks（後端按角色預設過濾）。
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import {
  Button,
  Form,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Search, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { AUDIT_TASK_STATUSES, userCan, type AuditTaskStatus } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { ListPageShell, FilterBar, FilterBarLabel, StatusPill } from '@/components/shell';
import ResponsiveModal from '@/components/common/ResponsiveModal';
import {
  COMPLETION_ALERT_DAYS,
  deriveReceiveStatus,
  getCompletionDueState,
  type ReceiveStatus,
} from '@/components/audit-tasks/task-list-helpers';

export const Route = createFileRoute('/_app/audit-tasks/')({ component: AuditTaskListPage });

// ─── 類型 ─────────────────────────────────────────────────────────────────────

/** 後端 GET /api/audit-tasks 列表項 */
interface AuditTaskListItem {
  id: number;
  caseId: number;
  caseCode: string | null;
  caseScheme: string | null;
  caseVerificationType: string | null;
  clientId: number | null;
  clientName: string | null;
  assigneeId: number;
  assigneeName: string | null;
  status: AuditTaskStatus;
  planCode: string | null;
  plannedAuditMonth: string | null;
  arrivedAt: string | null;
  leftAt: string | null;
  isTeamLead: boolean;
  createdAt: string;
  // ─── 序106：列表擴欄 ───
  /** 計畫狀態＝案件 workflowState */
  caseWorkflowState: string | null;
  /** 驗證類別（jsonb 字串陣列） */
  verificationCategories: string[] | null;
  /** 申請明細（解析 驗證品項/驗證選項/農戶數/驗證面積/通路） */
  applicationDetails: unknown;
  verificationNotes: string | null;
  plannedAuditDate: string | null;
  auditTeamNames: string | null;
  prevYearTeamNames: string | null;
  latestCertIssuedAt: string | null;
  latestCertExpiresAt: string | null;
  contactPhone: string | null;
  contactAddress: string | null;
  paidConfirmed: boolean;
  certIssued: boolean;
  // ─── 序48 ───
  /** 派案回覆（accepted / rejected / null＝尚未回覆） */
  dispatchResponse: string | null;
  /** 稽核完成日（稽核任務書完成時間，回落案件稽核完成期限；YYYY-MM-DD） */
  auditCompletionDate: string | null;
}

/**
 * 自申請明細（TOP/TAP 分流 jsonb）提取列表展示摘要（序106）。
 * 解析失敗或缺欄回 null，各欄前端顯示「—」。
 */
function summarizeApplicationDetails(details: unknown): {
  productItems: string | null;
  verificationOption: string | null;
  farmerCount: number | null;
  area: string | null;
  salesChannels: string | null;
} {
  const empty = {
    productItems: null,
    verificationOption: null,
    farmerCount: null,
    area: null,
    salesChannels: null,
  };
  if (!details || typeof details !== 'object') return empty;
  const d = details as Record<string, unknown>;
  // 品項：TOP productItems[{name}] / TAP rows[{productItem}]
  const items: string[] = [];
  if (Array.isArray(d.productItems)) {
    for (const it of d.productItems) {
      const name = (it as { name?: string })?.name;
      if (name) items.push(name);
    }
  }
  if (Array.isArray(d.rows)) {
    for (const r of d.rows) {
      const name = (r as { productItem?: string })?.productItem;
      if (name) items.push(name);
    }
  }
  const optionLabel =
    d.verificationOption === 'group'
      ? '集團'
      : d.verificationOption === 'individual'
        ? '個別'
        : null;
  const channelLabelMap: Record<string, string> = {
    auction: '拍賣市場',
    supermarket: '超市賣場',
    processor: '加工業者',
    distributor: '分裝/流通',
    other: '其他',
  };
  const channels = Array.isArray(d.salesChannels)
    ? (d.salesChannels as string[]).map((ch) => channelLabelMap[ch] ?? ch).join('、')
    : null;
  const area =
    (typeof d.verificationArea === 'string' && d.verificationArea) ||
    (typeof d.totalArea === 'string' && d.totalArea) ||
    null;
  return {
    productItems: items.length ? items.join('、') : null,
    verificationOption: optionLabel,
    farmerCount: typeof d.groupFarmerCount === 'number' ? d.groupFarmerCount : null,
    area: area ? `${area} 公頃` : null,
    salesChannels: channels || null,
  };
}

interface ListResponse {
  items: AuditTaskListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ─── 接收狀態樣式映射（序48） ───────────────────────────────────────────────

const RECEIVE_TONE: Record<ReceiveStatus, 'success' | 'warning' | 'danger'> = {
  待接收: 'warning',
  已接收: 'success',
  不接收: 'danger',
};

/** 列表每頁筆數（伺服器端分頁） */
const PAGE_SIZE = 20;

// ─── 補建任務 Modal ──────────────────────────────────────────────────────────

/**
 * 「補建任務」按鈕對應的 Modal（異常補救入口）。
 *
 * 使用情境：任務正常由派案「確認派遣」（confirmDispatch）後自動建立。
 * 僅當某既有已派案案件因故未自動建立（例如確認派遣時的 bootstrap 失敗），
 * admin/manager 才在此手動觸發 POST /api/audit-tasks/create-from-case 補救。
 */
/** 補建 Modal 中案件搜尋的最小列表項（取自 GET /api/cases?search=） */
interface BootstrapCaseOption {
  id: number;
  caseCode: string;
  clientName: string | null;
  workflowState: string | null;
}

function BootstrapTaskModal({ open, onCancel }: { open: boolean; onCancel: () => void }) {
  const qc = useQueryClient();
  const [form] = Form.useForm<{ caseId: number }>();

  // 搜尋詞 + debounce：避免每次按鍵都打 API
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedKeyword(keyword.trim()), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  // 案件下拉資料：依關鍵字搜尋（caseCode 或 clientName 部分匹配）
  // open 為 false 時不請求，避免無謂的網路；輸入詞清空時拉最近案件供使用者瀏覽
  const { data: casesData, isFetching: casesLoading } = useQuery<{
    items: BootstrapCaseOption[];
  }>({
    queryKey: ['bootstrap-cases', debouncedKeyword],
    queryFn: () => {
      const qs = new URLSearchParams({ pageSize: '50' });
      if (debouncedKeyword) qs.set('search', debouncedKeyword);
      return apiClient.get(`/api/cases?${qs.toString()}`);
    },
    enabled: open,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (input: { caseId: number }) =>
      apiClient.post<{
        created: boolean;
        taskIds: number[];
        leadTaskId: number | null;
        formInstanceIds: number[];
      }>('/api/audit-tasks/create-from-case', input),
    onSuccess: (res) => {
      if (res.created) {
        message.success(
          `已補建 ${res.taskIds.length} 筆任務（含 ${res.formInstanceIds.length} 份預設表單）`,
        );
      } else {
        message.info('該案件已有任務，無需補建');
      }
      onCancel();
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['audit-tasks'] });
    },
    onError: (err: { status?: number; message?: string }) => {
      if (err.status === 422) {
        message.error(err.message ?? '案件尚未派遣團隊，無法補建');
      } else if (err.status === 404) {
        message.error('案件不存在');
      } else {
        message.error('補建失敗');
      }
    },
  });

  return (
    <ResponsiveModal
      title="補建稽核任務"
      open={open}
      onCancel={() => {
        onCancel();
        form.resetFields();
        setKeyword('');
      }}
      footer={null}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        正常情況下，任務會在派案「確認派遣」後自動建立並流轉至此。僅當某已派案案件因故未自動建立時，才需在此手動補救。
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={(v) => mutation.mutate(v)}>
        <Form.Item
          name="caseId"
          label="案件"
          rules={[{ required: true, message: '請選擇案件' }]}
          extra="輸入案件編號或客戶名稱搜尋（部分匹配，最多顯示 50 筆）。"
        >
          <Select<number>
            showSearch
            placeholder="輸入案件編號或客戶名稱…"
            // 後端負責過濾，前端不再二次過濾，避免 cache 過期導致選項丟失
            filterOption={false}
            onSearch={setKeyword}
            // 關閉時清空關鍵字，下次重新打開不殘留
            onBlur={() => setKeyword('')}
            notFoundContent={casesLoading ? <Spin size="small" /> : '無符合案件'}
            loading={casesLoading}
            options={(casesData?.items ?? []).map((c) => ({
              value: c.id,
              label: `${c.caseCode} · ${c.clientName ?? '—'}${
                c.workflowState ? ` · ${c.workflowState}` : ''
              }`,
            }))}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          補建
        </Button>
      </Form>
    </ResponsiveModal>
  );
}

// ─── 主頁面 ──────────────────────────────────────────────────────────────────

function AuditTaskListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // 不具 case.read.all（現場稽核員）強制只能看自己；
  // 管理組（admin/manager/decider）預設「全部」，可切「我的」。
  // user 未恢復（首帧尚未恢復）時不鎖，避免管理組被誤鎖在「我的」而看到空列表；
  // 稽核員的行級過濾由後端強制，不依賴此 UI 開關。
  // userCan：有 capabilities 時以能力集為準；無則回落 roles → DEFAULT_ROLE_CAPABILITIES
  const auditorLocked = !!user && !userCan(user, 'case.read.all');
  const [scope, setScope] = useState<'mine' | 'all'>(auditorLocked ? 'mine' : 'all');
  const [statusFilter, setStatusFilter] = useState<AuditTaskStatus | undefined>(undefined);
  // 伺服器端分頁頁碼：視角/狀態過濾變化時重置為 1（見下方 onChange）
  const [page, setPage] = useState(1);
  const [showBootstrap, setShowBootstrap] = useState(false);

  // 使用者資料晚於首帧恢復時，補鎖現場稽核員的視角到「我的」
  useEffect(() => {
    if (auditorLocked) setScope('mine');
  }, [auditorLocked]);

  // 「補建任務」入口對齊後端 create-from-case 的能力 gate（case.dispatch）
  const isManager = user ? userCan(user, 'case.dispatch') : false;

  const queryParams = new URLSearchParams();
  if (scope === 'mine') queryParams.set('mine', '1');
  if (statusFilter) queryParams.set('status', statusFilter);
  queryParams.set('page', String(page));
  queryParams.set('pageSize', String(PAGE_SIZE));

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['audit-tasks', scope, statusFilter, page, PAGE_SIZE],
    queryFn: () => apiClient.get(`/api/audit-tasks?${queryParams.toString()}`),
  });

  const items = data?.items ?? [];

  /* 序48（2026-09-25）：列表欄位順序——任務/接收狀態/計畫編號/客戶名稱/驗證方案/驗證類型/
     計畫狀態/驗證類別/驗證品項/驗證選項/預定稽核日期/稽核完成時間/稽核小組，其後沿用序106
     的農戶數/驗證面積/上年度稽核小組/最新發證日期/證書到期日/聯絡電話/通訊地址/通路/
     繳費狀態/證書發證/驗證組備註 */
  const dash = <Typography.Text type="secondary">—</Typography.Text>;
  const columns: ColumnsType<AuditTaskListItem> = [
    /* 任務入口列（保留：點擊進入 + 組長標記） */
    {
      title: '任務',
      dataIndex: 'id',
      width: 90,
      fixed: 'left',
      render: (id: number, record) => (
        <Space size={6}>
          <span style={{ fontFamily: 'monospace' }}>#{id}</span>
          {record.isTeamLead && <Tag color="gold">組長</Tag>}
        </Space>
      ),
    },
    {
      title: '接收狀態',
      key: 'receiveStatus',
      width: 100,
      render: (_, record) => {
        const rs = deriveReceiveStatus(record);
        return (
          <StatusPill tone={RECEIVE_TONE[rs]} dot>
            {rs}
          </StatusPill>
        );
      },
    },
    {
      title: '計畫編號',
      dataIndex: 'planCode',
      width: 150,
      render: (v: string | null, record) => (
        <span style={{ fontFamily: 'monospace' }}>{v ?? record.caseCode ?? '—'}</span>
      ),
    },
    {
      title: '客戶名稱',
      dataIndex: 'clientName',
      width: 160,
      ellipsis: true,
      render: (n: string | null) => n ?? dash,
    },
    {
      title: '驗證方案',
      dataIndex: 'caseScheme',
      width: 90,
      render: (s: string | null) => (s ? <Tag>{s}</Tag> : dash),
    },
    {
      title: '驗證類型',
      dataIndex: 'caseVerificationType',
      width: 120,
      ellipsis: true,
      render: (v: string | null) => v ?? dash,
    },
    {
      title: '計畫狀態',
      dataIndex: 'caseWorkflowState',
      width: 100,
      render: (v: string | null) => (v ? <Tag>{v}</Tag> : dash),
    },
    {
      title: '驗證類別',
      dataIndex: 'verificationCategories',
      width: 120,
      ellipsis: true,
      render: (v: string[] | null) => (Array.isArray(v) && v.length ? v.join('、') : dash),
    },
    {
      title: '驗證品項',
      key: 'productItems',
      width: 150,
      ellipsis: true,
      render: (_, record) =>
        summarizeApplicationDetails(record.applicationDetails).productItems ?? dash,
    },
    {
      title: '驗證選項',
      key: 'verificationOption',
      width: 90,
      render: (_, record) =>
        summarizeApplicationDetails(record.applicationDetails).verificationOption ?? dash,
    },
    {
      title: '預定稽核日期',
      dataIndex: 'plannedAuditDate',
      width: 120,
      render: (v: string | null, record) => v ?? record.plannedAuditMonth ?? dash,
    },
    {
      /* 稽核完成時間＝稽核任務書「完成時間（年月日前）」；到達前 7 天起（含逾期）紅色提示 */
      title: '稽核完成時間',
      dataIndex: 'auditCompletionDate',
      width: 130,
      render: (v: string | null, record) => {
        if (!v) return dash;
        const due = getCompletionDueState(v, record.status);
        if (!due.alert) return v;
        const hint =
          due.daysLeft !== null && due.daysLeft < 0
            ? `已逾期 ${-due.daysLeft} 天`
            : `剩 ${due.daysLeft ?? 0} 天`;
        return (
          <Tooltip title={`距稽核完成日 ${COMPLETION_ALERT_DAYS} 天內，請留意進度（${hint}）`}>
            <Typography.Text type="danger" strong>
              {v}
              <span style={{ fontSize: 12, marginLeft: 4 }}>（{hint}）</span>
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: '稽核小組',
      dataIndex: 'auditTeamNames',
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v ?? dash,
    },
    {
      title: '農戶數',
      key: 'farmerCount',
      width: 80,
      render: (_, record) =>
        summarizeApplicationDetails(record.applicationDetails).farmerCount ?? dash,
    },
    {
      title: '驗證面積',
      key: 'area',
      width: 110,
      render: (_, record) => summarizeApplicationDetails(record.applicationDetails).area ?? dash,
    },
    {
      title: '上年度稽核小組',
      dataIndex: 'prevYearTeamNames',
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v ?? dash,
    },
    {
      title: '最新發證日期',
      dataIndex: 'latestCertIssuedAt',
      width: 120,
      render: (v: string | null) => (v ? String(v).slice(0, 10) : dash),
    },
    {
      title: '證書到期日',
      dataIndex: 'latestCertExpiresAt',
      width: 120,
      render: (v: string | null) => (v ? String(v).slice(0, 10) : dash),
    },
    {
      title: '聯絡電話',
      dataIndex: 'contactPhone',
      width: 120,
      render: (v: string | null) => v ?? dash,
    },
    {
      title: '通訊地址',
      dataIndex: 'contactAddress',
      width: 200,
      ellipsis: true,
      render: (v: string | null) => v ?? dash,
    },
    {
      title: '通路',
      key: 'salesChannels',
      width: 130,
      ellipsis: true,
      render: (_, record) =>
        summarizeApplicationDetails(record.applicationDetails).salesChannels ?? dash,
    },
    {
      title: '繳費狀態',
      dataIndex: 'paidConfirmed',
      width: 90,
      render: (v: boolean) => (
        <StatusPill tone={v ? 'success' : 'warning'} size="sm">
          {v ? '已繳費' : '未繳費'}
        </StatusPill>
      ),
    },
    {
      title: '證書發證',
      dataIndex: 'certIssued',
      width: 90,
      render: (v: boolean) => (
        <StatusPill tone={v ? 'success' : 'muted'} size="sm">
          {v ? '已發證' : '未發證'}
        </StatusPill>
      ),
    },
    {
      title: '驗證組備註',
      dataIndex: 'verificationNotes',
      width: 180,
      ellipsis: true,
      render: (v: string | null) => v ?? dash,
    },
  ];

  return (
    <ListPageShell
      title="稽/審核流程管理"
      description="派案完成後自動建立任務，點選任務進入流程化作業：文件審查→計劃→現場→檢驗→報告編製→結案。"
      actions={
        <Space>
          {/* 補建任務為異常補救入口（任務正常由派案確認派遣後自動流轉），故降為次要按鈕、不搶主視覺 */}
          {isManager && (
            <Button icon={<Wrench size={14} />} onClick={() => setShowBootstrap(true)}>
              補建任務
            </Button>
          )}
        </Space>
      }
      filters={
        <FilterBar
          filters={
            <Space size={12} wrap>
              <Space size={6}>
                <FilterBarLabel>視角</FilterBarLabel>
                <Segmented
                  value={scope}
                  onChange={(v) => {
                    setScope(v as 'mine' | 'all');
                    setPage(1);
                  }}
                  disabled={auditorLocked}
                  options={[
                    { label: '我的', value: 'mine' },
                    { label: '全部', value: 'all' },
                  ]}
                />
              </Space>
              <Space size={6}>
                <FilterBarLabel>任務狀態</FilterBarLabel>
                <Select<AuditTaskStatus | undefined>
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v ?? undefined);
                    setPage(1);
                  }}
                  allowClear
                  placeholder="全部狀態"
                  style={{ width: 160 }}
                  options={AUDIT_TASK_STATUSES.map((s) => ({ value: s, label: s }))}
                  suffixIcon={<Search size={14} />}
                />
              </Space>
            </Space>
          }
        />
      }
    >
      <Table<AuditTaskListItem>
        rowKey="id"
        loading={isLoading}
        dataSource={items}
        columns={columns}
        /* 小螢幕保留橫向滾動兜底，配合 column.responsive 隱藏列做雙保險 */
        scroll={{ x: 'max-content' }}
        /* 伺服器端分頁：total 取後端回傳，換頁更新 page（進 queryKey 重新請求） */
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p) => setPage(p),
        }}
        onRow={(record) => ({
          onClick: () => void navigate({ to: `/audit-tasks/${record.id}` }),
          style: { cursor: 'pointer' },
        })}
      />

      <BootstrapTaskModal open={showBootstrap} onCancel={() => setShowBootstrap(false)} />
    </ListPageShell>
  );
}
